import { useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { MdOutlineAccessTime, MdOutlineBadge, MdOutlineBuild, MdOutlineDashboard, MdOutlineDescription, MdOutlineInventory2, MdOutlinePeople, MdOutlinePointOfSale } from "react-icons/md";
import { LuChevronRight, LuPanelLeftClose, LuPanelRightClose } from "react-icons/lu";
import "./SalesSideTabs.scss";

interface SalesSideTabsProps {
  onNavigate: () => void;
}

const menuItems = [
  { name: "Dashboard", path: "/app/sales/dashboard", icon: MdOutlineDashboard },
  { name: "Kunden", path: "/app/sales/kunden", icon: MdOutlinePeople, expandable: true },
  { name: "Verträge", path: "/app/sales/vertraege", icon: MdOutlineDescription, expandable: true },
  { name: "Reparatur", path: "/app/sales/reparatur", icon: MdOutlineBuild, expandable: true },
  { name: "Kasse", path: "/app/sales/cash-register", icon: MdOutlinePointOfSale, expandable: true },
  { name: "Lager", path: "/app/sales/lager", icon: MdOutlineInventory2, expandable: true },
  { name: "Mitarbeiter", path: "/app/sales/mitarbeiter", icon: MdOutlineBadge, expandable: true },
  { name: "Zeit", path: "/app/sales/zeit", icon: MdOutlineAccessTime,  expandable: true }
];

export default function SalesSideTabs({ onNavigate }: SalesSideTabsProps) {
  const { pathname } = useLocation();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState<Record<string, boolean>>(() => ({
    "/app/sales/kunden": pathname.startsWith("/app/sales/kunden/"),
    "/app/sales/vertraege": pathname.startsWith("/app/sales/vertraege/"),
    "/app/sales/lager": pathname.startsWith("/app/sales/lager/"),
    "/app/sales/mitarbeiter": pathname.startsWith("/app/sales/mitarbeiter/"),
    "/app/sales/reparatur": pathname.startsWith("/app/sales/reparatur/"),
    "/app/sales/zeit": pathname.startsWith("/app/sales/zeit/"),
    "/app/sales/cash-register": pathname.startsWith("/app/sales/cash-register/"),
  }));

  return (
    <nav className={`sales-side-tabs ${isCollapsed ? "sales-side-tabs--collapsed" : ""}`} aria-label="Sales-Navigation">
      <div className="sales-side-tabs__menu">
        <button
          type="button"
          className="sales-side-tabs__toggle"
          onClick={() => setIsCollapsed((previous) => !previous)}
          title={isCollapsed ? "Ausklappen" : "Einklappen"}
          aria-label={isCollapsed ? "Sidebar ausklappen" : "Sidebar einklappen"}
          aria-expanded={!isCollapsed}
        >
          {isCollapsed ? <LuPanelRightClose className="sales-side-tabs__icon" /> : <LuPanelLeftClose className="sales-side-tabs__icon" />}
          <span className="sales-side-tabs__label">Einklappen</span>
        </button>
        <div className="sales-side-tabs__items">
          {menuItems.map(({ name, path, icon: Icon, expandable }) => {
            const isExpanded = Boolean(expandedMenus[path]);
            const submenuId = `sales-submenu-${path.split("/").at(-1)}`;

            return (
              <div key={path} className="sales-side-tabs__group">
                <div className={`sales-side-tabs__group-header ${expandable ? "sales-side-tabs__group-header--expandable" : ""}`}>
                  <NavLink
                    to={path}
                    onClick={onNavigate}
                    end={!expandable}
                    className={({ isActive }) => `sales-side-tabs__item ${isActive ? "sales-side-tabs__item--active" : ""}`}
                    title={isCollapsed ? name : undefined}
                    aria-label={name}
                  >
                    <Icon className="sales-side-tabs__icon" />
                    <span className="sales-side-tabs__label">{name}</span>
                  </NavLink>
                  {expandable && (
                    <button
                      type="button"
                      className={`sales-side-tabs__submenu-toggle ${isExpanded ? "sales-side-tabs__submenu-toggle--expanded" : ""}`}
                      onClick={() => setExpandedMenus((previous) => ({ ...previous, [path]: !previous[path] }))}
                      aria-label={`${name}: Untermenü ${isExpanded ? "einklappen" : "ausklappen"}`}
                      aria-expanded={isExpanded}
                      aria-controls={submenuId}
                    >
                      <LuChevronRight aria-hidden="true" />
                    </button>
                  )}
                </div>
                {expandable && (
                  <div id={submenuId} className="sales-side-tabs__submenu" hidden={!isExpanded}>
                    {path === "/app/sales/kunden" && (
                      <>
                        <NavLink
                          to="/app/sales/kunden/kundenliste"
                          onClick={onNavigate}
                          className={({ isActive }) => `sales-side-tabs__submenu-item ${isActive ? "sales-side-tabs__submenu-item--active" : ""}`}
                        >
                          Kundenliste
                        </NavLink>
                        <NavLink
                          to="/app/sales/kunden/kundenauswertung"
                          onClick={onNavigate}
                          className={({ isActive }) => `sales-side-tabs__submenu-item ${isActive ? "sales-side-tabs__submenu-item--active" : ""}`}
                        >
                          Kundenauswertung
                        </NavLink>
                      </>
                    )}
                    {path === "/app/sales/vertraege" && (
                      <>
                        <NavLink
                          to="/app/sales/vertraege/vvl-liste"
                          onClick={onNavigate}
                          className={({ isActive }) => `sales-side-tabs__submenu-item ${isActive ? "sales-side-tabs__submenu-item--active" : ""}`}
                        >
                          VVL-Liste
                        </NavLink>
                        <NavLink
                          to="/app/sales/vertraege/vertragsuebersicht"
                          onClick={onNavigate}
                          className={({ isActive }) => `sales-side-tabs__submenu-item ${isActive ? "sales-side-tabs__submenu-item--active" : ""}`}
                        >
                          Vertragsübersicht
                        </NavLink>
                        <NavLink
                          to="/app/sales/vertraege/vvl-kampagnen"
                          onClick={onNavigate}
                          className={({ isActive }) => `sales-side-tabs__submenu-item ${isActive ? "sales-side-tabs__submenu-item--active" : ""}`}
                        >
                          VVL-Kampagnen
                        </NavLink>
                      </>
                    )}
                    {path === "/app/sales/lager" && (
                      <>
                        <NavLink
                          to="/app/sales/lager/artikelverwaltung"
                          onClick={onNavigate}
                          className={({ isActive }) => `sales-side-tabs__submenu-item ${isActive ? "sales-side-tabs__submenu-item--active" : ""}`}
                        >
                          Artikelverwaltung
                        </NavLink>
                        <NavLink
                          to="/app/sales/lager/wareneingang-buchen"
                          onClick={onNavigate}
                          className={({ isActive }) => `sales-side-tabs__submenu-item ${isActive ? "sales-side-tabs__submenu-item--active" : ""}`}
                        >
                          Wareneingang buchen
                        </NavLink>
                        <NavLink
                          to="/app/sales/lager/warenausgang-buchen"
                          onClick={onNavigate}
                          className={({ isActive }) => `sales-side-tabs__submenu-item ${isActive ? "sales-side-tabs__submenu-item--active" : ""}`}
                        >
                          Warenausgang buchen
                        </NavLink>
                        <NavLink
                          to="/app/sales/lager/wareneingaenge"
                          onClick={onNavigate}
                          className={({ isActive }) => `sales-side-tabs__submenu-item ${isActive ? "sales-side-tabs__submenu-item--active" : ""}`}
                        >
                          Alle Wareneingänge
                        </NavLink>
                        <NavLink
                          to="/app/sales/lager/warenausgaenge"
                          onClick={onNavigate}
                          className={({ isActive }) => `sales-side-tabs__submenu-item ${isActive ? "sales-side-tabs__submenu-item--active" : ""}`}
                        >
                          Alle Warenausgänge
                        </NavLink>
                        <NavLink
                          to="/app/sales/lager/etiketten-drucken"
                          onClick={onNavigate}
                          className={({ isActive }) => `sales-side-tabs__submenu-item ${isActive ? "sales-side-tabs__submenu-item--active" : ""}`}
                        >
                          Etiketten drucken
                        </NavLink>
                        <NavLink
                          to="/app/sales/lager/imei-suche"
                          onClick={onNavigate}
                          className={({ isActive }) => `sales-side-tabs__submenu-item ${isActive ? "sales-side-tabs__submenu-item--active" : ""}`}
                        >
                          IMEI Suche
                        </NavLink>
                        <NavLink
                          to="/app/sales/lager/mengen-inventur"
                          onClick={onNavigate}
                          className={({ isActive }) => `sales-side-tabs__submenu-item ${isActive ? "sales-side-tabs__submenu-item--active" : ""}`}
                        >
                          Mengen Inventur
                        </NavLink>
                        <NavLink
                          to="/app/sales/lager/seriennummern-inventur"
                          onClick={onNavigate}
                          className={({ isActive }) => `sales-side-tabs__submenu-item ${isActive ? "sales-side-tabs__submenu-item--active" : ""}`}
                        >
                          Seriennr Inventur
                        </NavLink>
                        <NavLink
                          to="/app/sales/lager/ean-inventur"
                          onClick={onNavigate}
                          className={({ isActive }) => `sales-side-tabs__submenu-item ${isActive ? "sales-side-tabs__submenu-item--active" : ""}`}
                        >
                          EAN Inventur
                        </NavLink>
                        <NavLink
                          to="/app/sales/lager/lagerwert"
                          onClick={onNavigate}
                          className={({ isActive }) => `sales-side-tabs__submenu-item ${isActive ? "sales-side-tabs__submenu-item--active" : ""}`}
                        >
                          Lagerwert
                        </NavLink>
                        <NavLink
                          to="/app/sales/lager/inventuren"
                          onClick={onNavigate}
                          className={({ isActive }) => `sales-side-tabs__submenu-item ${isActive ? "sales-side-tabs__submenu-item--active" : ""}`}
                        >
                          Inventuren
                        </NavLink>
                        <NavLink
                          to="/app/sales/lager/hitliste"
                          onClick={onNavigate}
                          className={({ isActive }) => `sales-side-tabs__submenu-item ${isActive ? "sales-side-tabs__submenu-item--active" : ""}`}
                        >
                          Hitliste
                        </NavLink>
                        <NavLink
                          to="/app/sales/lager/schwebelager"
                          onClick={onNavigate}
                          className={({ isActive }) => `sales-side-tabs__submenu-item ${isActive ? "sales-side-tabs__submenu-item--active" : ""}`}
                        >
                          Schwebelager
                        </NavLink>
                      </>
                    )}
                    {path === "/app/sales/reparatur" && (
                      <NavLink
                        to="/app/sales/reparatur/reparaturauftrag"
                        onClick={onNavigate}
                        className={({ isActive }) => `sales-side-tabs__submenu-item ${isActive ? "sales-side-tabs__submenu-item--active" : ""}`}
                      >
                        Reparaturauftrag
                      </NavLink>
                    )}
                    {path === "/app/sales/cash-register" && (
                      <>
                        <NavLink
                          to="/app/sales/cash-register/neue-entnahme"
                          onClick={onNavigate}
                          className={({ isActive }) => `sales-side-tabs__submenu-item ${isActive ? "sales-side-tabs__submenu-item--active" : ""}`}
                        >
                          Neue Entnahme
                        </NavLink>
                        <NavLink
                          to="/app/sales/cash-register/abschluesse"
                          onClick={onNavigate}
                          className={({ isActive }) => `sales-side-tabs__submenu-item ${isActive ? "sales-side-tabs__submenu-item--active" : ""}`}
                        >
                          Abschlüsse
                        </NavLink>
                        <NavLink
                          to="/app/sales/cash-register/kassenbericht"
                          onClick={onNavigate}
                          className={({ isActive }) => `sales-side-tabs__submenu-item ${isActive ? "sales-side-tabs__submenu-item--active" : ""}`}
                        >
                          Kassenbericht
                        </NavLink>
                        <NavLink
                          to="/app/sales/cash-register/beleghistorie"
                          onClick={onNavigate}
                          className={({ isActive }) => `sales-side-tabs__submenu-item ${isActive ? "sales-side-tabs__submenu-item--active" : ""}`}
                        >
                          Beleghistorie
                        </NavLink>
                        <NavLink
                          to="/app/sales/cash-register/belegdetails"
                          onClick={onNavigate}
                          className={({ isActive }) => `sales-side-tabs__submenu-item ${isActive ? "sales-side-tabs__submenu-item--active" : ""}`}
                        >
                          Belegdetails
                        </NavLink>
                        <NavLink
                          to="/app/sales/cash-register/posten"
                          onClick={onNavigate}
                          className={({ isActive }) => `sales-side-tabs__submenu-item ${isActive ? "sales-side-tabs__submenu-item--active" : ""}`}
                        >
                          Posten
                        </NavLink>
                        <NavLink
                          to="/app/sales/cash-register/angebote"
                          onClick={onNavigate}
                          className={({ isActive }) => `sales-side-tabs__submenu-item ${isActive ? "sales-side-tabs__submenu-item--active" : ""}`}
                        >
                          Angebote
                        </NavLink>
                        <NavLink
                          to="/app/sales/cash-register/kasse-eroeffnen"
                          onClick={onNavigate}
                          className={({ isActive }) => `sales-side-tabs__submenu-item ${isActive ? "sales-side-tabs__submenu-item--active" : ""}`}
                        >
                          Kasse eröffnen
                        </NavLink>
                        <NavLink
                          to="/app/sales/cash-register/rechnung-schreiben"
                          onClick={onNavigate}
                          className={({ isActive }) => `sales-side-tabs__submenu-item ${isActive ? "sales-side-tabs__submenu-item--active" : ""}`}
                        >
                          Rechnung schreiben
                        </NavLink>
                      </>
                    )}
                    {path === "/app/sales/mitarbeiter" && (
                      <>
                      <NavLink
                          to="/app/sales/mitarbeiter/tarifuebersicht"
                          onClick={onNavigate}
                          className={({ isActive }) => `sales-side-tabs__submenu-item ${isActive ? "sales-side-tabs__submenu-item--active" : ""}`}
                        >
                          Tarifübersicht
                        </NavLink>
                        <NavLink
                          to="/app/sales/mitarbeiter/vertraege-je-mitarbeiter"
                          onClick={onNavigate}
                          className={({ isActive }) => `sales-side-tabs__submenu-item ${isActive ? "sales-side-tabs__submenu-item--active" : ""}`}
                        >
                          Verträge je Mitarbeiter
                        </NavLink>
                        <NavLink
                          to="/app/sales/mitarbeiter/rechte"
                          onClick={onNavigate}
                          className={({ isActive }) => `sales-side-tabs__submenu-item ${isActive ? "sales-side-tabs__submenu-item--active" : ""}`}
                        >
                          Rechte
                        </NavLink>

                        <NavLink
                          to="/app/sales/mitarbeiter/benachrichtigungen"
                          onClick={onNavigate}
                          className={({ isActive }) => `sales-side-tabs__submenu-item ${isActive ? "sales-side-tabs__submenu-item--active" : ""}`}
                        >
                          Benachrichtigungen
                        </NavLink>
                      </>
                    )}
                    {path === "/app/sales/zeit" && (
                      <>
                        <NavLink
                          to="/app/sales/zeit/zeiterfassung"
                          onClick={onNavigate}
                          className={({ isActive }) => `sales-side-tabs__submenu-item ${isActive ? "sales-side-tabs__submenu-item--active" : ""}`}
                        >
                          Zeiterfassung
                        </NavLink>
                        <NavLink
                          to="/app/sales/zeit/wochenuebersicht"
                          onClick={onNavigate}
                          className={({ isActive }) => `sales-side-tabs__submenu-item ${isActive ? "sales-side-tabs__submenu-item--active" : ""}`}
                        >
                          Wochenübersicht
                        </NavLink>
                        <NavLink
                          to="/app/sales/zeit/monatsuebersicht"
                          onClick={onNavigate}
                          className={({ isActive }) => `sales-side-tabs__submenu-item ${isActive ? "sales-side-tabs__submenu-item--active" : ""}`}
                        >
                          Monatsübersicht
                        </NavLink>
                        <NavLink
                          to="/app/sales/zeit/stunden-bearbeiten"
                          onClick={onNavigate}
                          className={({ isActive }) => `sales-side-tabs__submenu-item ${isActive ? "sales-side-tabs__submenu-item--active" : ""}`}
                        >
                          Stunden bearbeiten
                        </NavLink>
                        <NavLink
                          to="/app/sales/zeit/einstellungen"
                          onClick={onNavigate}
                          className={({ isActive }) => `sales-side-tabs__submenu-item ${isActive ? "sales-side-tabs__submenu-item--active" : ""}`}
                        >
                          Einstellungen
                        </NavLink>
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </nav>
  );
}
